/**
 * Chat Service
 * Handles GraphQL mutations and subscriptions for chatbot
 */

import { generateClient } from 'aws-amplify/api';
import { ChatResponse, SendChatMessageInput } from '../types/chat';

const client = generateClient();

// GraphQL mutation for sending chat messages
const sendChatMessageMutation = `
  mutation SendChatMessage($sessionId: ID, $message: String!) {
    sendChatMessage(sessionId: $sessionId, message: $message) {
      sessionId
      intent
      message {
        id
        sessionId
        role
        content
        intent
        createdAt
      }
    }
  }
`;

/**
 * Send a chat message to the AI assistant
 */
export async function sendChatMessage(
  input: SendChatMessageInput
): Promise<ChatResponse> {
  try {
    console.log('[ChatService] Sending message:', input);
    
    const result = await client.graphql({
      query: sendChatMessageMutation,
      variables: input
    });

    console.log('[ChatService] Raw result:', JSON.stringify(result, null, 2));
    console.log('[ChatService] Result keys:', Object.keys(result));
    console.log('[ChatService] Has data?', 'data' in result);
    console.log('[ChatService] Has errors?', 'errors' in result);

    // Check for GraphQL errors first
    if ('errors' in result && result.errors && result.errors.length > 0) {
      console.error('[ChatService] GraphQL errors:', result.errors);
      const firstError = result.errors[0];
      throw new Error(firstError.message || 'GraphQL error occurred');
    }

    // Type guard to check if result has data property
    if ('data' in result && result.data?.sendChatMessage) {
      const response = result.data.sendChatMessage as ChatResponse;
      console.log('[ChatService] Success! Response:', response);
      console.log('[ChatService] Message content type:', typeof response.message.content);
      console.log('[ChatService] Message content preview:', 
        response.message.content.substring(0, 200) + '...');
      return response;
    }

    console.error('[ChatService] No data in result:', result);
    throw new Error('No response from chatbot - please check Lambda logs');
  } catch (error) {
    console.error('[ChatService] Error sending message:', error);
    
    // Log more details about the error
    if (error && typeof error === 'object') {
      console.error('[ChatService] Error details:', {
        message: (error as any).message,
        errors: (error as any).errors,
        data: (error as any).data,
        name: (error as any).name,
        stack: (error as any).stack
      });
      
      // Check for GraphQL errors
      if ((error as any).errors && Array.isArray((error as any).errors)) {
        console.error('[ChatService] GraphQL errors:', (error as any).errors);
        const firstError = (error as any).errors[0];
        if (firstError) {
          // Extract meaningful error message
          const errorMsg = firstError.message || 'GraphQL error occurred';
          throw new Error(`Chatbot error: ${errorMsg}`);
        }
      }
    }
    
    // Re-throw with better message
    if (error instanceof Error) {
      throw new Error(`Failed to send message: ${error.message}`);
    }
    
    throw new Error('Failed to send message. Please try again.');
  }
}
